import consumer from "../channels/consumer"

// Only connect to posts channel on pages that need real-time updates
const isPostsPage = () => {
  const path = window.location.pathname
  return path === '/' || 
         path.startsWith('/posts') || 
         path.includes('/posts/')
}

// Check if user is authenticated
const isAuthenticated = () => {
  return document.querySelector('meta[name="current-user-id"]') !== null
}

// Only create subscription if we're on a posts-related page AND user is authenticated
if (isPostsPage() && isAuthenticated()) {
  consumer.subscriptions.create("PostsChannel", {
  connected() {
    console.log("📺 Connected to posts channel")
    
    // Show connection status
    this.showConnectionStatus('Connected to live feed', 'success')
  },

  disconnected() {
    console.log("📺 Disconnected from posts channel")
    
    // Show disconnection status
    this.showConnectionStatus('Disconnected from live feed', 'warning')
  },

  received(data) {
    console.log("📺 Received real-time update:", data)
    
    // Handle Turbo Stream broadcasts
    if (data.turbo_stream) {
      console.log("🔄 Processing Turbo Stream broadcast:", data.action)
      Turbo.renderStreamMessage(data.turbo_stream)
      
      // Add special handling for real-time updates
      if (data.action === 'comments_updated') {
        // Simple comment updates for posts#index - no special handling needed, just process the stream
        console.log('💬 Comments updated for post', data.post_id)
      } else if (data.action === 'show_comments_updated') {
        // Comment updates for posts#show - handled by individual post controllers
        console.log('💬 Show page comments updated for post', data.post_id)
      } else if (data.action === 'axle_reply') {
        this.handleAxleReply(data)
      } else if (data.action === 'comment_vote_update') {
        this.handleCommentVoteUpdate(data)
      } else if (data.action === 'post_stats_updated') {
        console.log('📊 Post stats updated for post', data.post_id)
      }
      return
    }
    
    // Legacy handling for non-Turbo Stream messages
    switch(data.action) {
      case 'new_post':
        this.handleNewPost(data)
        break
      case 'post_liked':
        this.handlePostLiked(data)
        break
      case 'post_updated':
        this.handlePostUpdated(data)
        break
      case 'new_comment':
        this.handleNewComment(data)
        break
    }
  },

  handleNewPost(data) {
    // Show toast notification
    this.showToast(`New post by ${data.author}`, 'info')
    
    // Update "new posts available" indicator if on page > 1
    const newPostsController = document.querySelector('[data-controller*="new-posts"]')
    if (newPostsController && window.location.search.includes('page=')) {
      // Trigger new posts indicator
      const application = window.Stimulus || this.application
      const controller = application.getControllerForElementAndIdentifier(newPostsController, 'new-posts')
      if (controller) {
        controller.showNewPostsAvailable()
      }
    }
    
    // Add subtle page title notification
    this.updatePageTitle('(1) New Post')
  },

  handlePostLiked(data) {
    // Update like count in real-time for other users
    const postStatsElement = document.getElementById(`post-stats-${data.post_id}`)
    if (postStatsElement) {
      const likeCountElement = postStatsElement.querySelector('[data-optimistic-like-target="count"]')
      if (likeCountElement) {
        const currentCount = parseInt(likeCountElement.textContent) || 0
        const newCount = data.liked ? currentCount + 1 : currentCount - 1
        likeCountElement.textContent = Math.max(0, newCount)
        
        // Add subtle animation
        likeCountElement.classList.add('animate-pulse')
        setTimeout(() => {
          likeCountElement.classList.remove('animate-pulse')
        }, 1000)
      }
    }
  },

  handlePostUpdated(data) {
    // Show toast for post edits
    this.showToast('A post was updated', 'info')
  },

  handleNewComment(data) {
    // Update comment count
    const postStatsElement = document.getElementById(`post-stats-${data.post_id}`)
    if (postStatsElement) {
      const commentLink = postStatsElement.querySelector('a[href*="/posts/"]')
      if (commentLink) {
        const commentCountSpan = commentLink.querySelector('span')
        if (commentCountSpan) {
          const currentCount = parseInt(commentCountSpan.textContent) || 0
          commentCountSpan.textContent = currentCount + 1
          
          // Add subtle animation
          commentCountSpan.classList.add('animate-bounce')
          setTimeout(() => {
            commentCountSpan.classList.remove('animate-bounce')
          }, 1000)
        }
      }
    }
    
    this.showToast(`New comment on "${data.post_title}"`, 'info')
  },

  showConnectionStatus(message, type) {
    const dot = document.getElementById('connection-status-dot')
    if (!dot) return
    dot.className = 'status-dot ' + (
      type === 'success' ? 'status-dot-connected' :
      type === 'warning' ? 'status-dot-disconnected' :
      type === 'error' ? 'status-dot-error' : 'status-dot-connecting'
    )
    dot.title = message
  },

  showToast(message, type = 'info') {
    // Find or create toast container
    let toastContainer = document.getElementById('toast-container')
    if (!toastContainer) {
      toastContainer = document.createElement('div')
      toastContainer.id = 'toast-container'
      toastContainer.className = 'fixed bottom-4 right-4 z-50 space-y-2'
      document.body.appendChild(toastContainer)
    }
    
    // Create toast
    const toast = document.createElement('div')
    toast.className = `flex items-center px-4 py-3 rounded-lg shadow-lg text-white transform transition-all duration-300 translate-x-full ${
      type === 'success' ? 'bg-green-500' : 
      type === 'error' ? 'bg-red-500' : 
      type === 'warning' ? 'bg-orange-500' : 'bg-blue-500'
    }`
    
    toast.innerHTML = `
      <i class="fas fa-${
        type === 'success' ? 'check-circle' : 
        type === 'error' ? 'exclamation-triangle' : 
        type === 'warning' ? 'exclamation-circle' : 'info-circle'
      } mr-2"></i>
      <span>${message}</span>
    `
    
    toastContainer.appendChild(toast)
    
    // Animate in
    requestAnimationFrame(() => {
      toast.classList.remove('translate-x-full')
      toast.classList.add('translate-x-0')
    })
    
    // Auto remove after 4 seconds
    setTimeout(() => {
      toast.classList.add('translate-x-full')
      toast.classList.remove('translate-x-0')
      setTimeout(() => {
        toast.remove()
        
        // Remove container if empty
        if (toastContainer.children.length === 0) {
          toastContainer.remove()
        }
      }, 300)
    }, 4000)
  },

  updatePageTitle(prefix) {
    const originalTitle = document.title.replace(/^\(\d+\)\s/, '')
    document.title = `${prefix} ${originalTitle}`
    
    // Reset after 5 seconds
    setTimeout(() => {
      document.title = originalTitle
    }, 5000)
  },

  handleRealTimeComment(data) {
    // Add subtle animation to newly added comments
    setTimeout(() => {
      let newComment
      if (data.action === 'new_reply') {
        // Find the most recent comment in the replies section
        const repliesContainer = document.querySelector(`#replies-${data.parent_id}`)
        if (repliesContainer) {
          newComment = repliesContainer.lastElementChild
        }
      } else {
        // Find the most recent comment in the main list
        const commentsContainer = document.querySelector(`[id^="comments-list-"]`)
        if (commentsContainer) {
          const firstComment = commentsContainer.querySelector('[data-post-item], .divide-y > div:first-child, .space-y-4 > div:first-child')
          if (firstComment) {
            newComment = firstComment
          }
        }
      }
      
      // Add subtle highlight animation
      if (newComment && !newComment.classList.contains('animate-pulse')) {
        newComment.style.backgroundColor = 'rgba(59, 130, 246, 0.1)'
        newComment.style.transition = 'background-color 2s ease-out'
        
        setTimeout(() => {
          newComment.style.backgroundColor = ''
          setTimeout(() => {
            newComment.style.transition = ''
          }, 2000)
        }, 100)
        
        console.log('✨ Highlighted new real-time comment')
      }
    }, 50) // Small delay to ensure DOM is updated
    
    // Haptic feedback on mobile
    if ('vibrate' in navigator) {
      navigator.vibrate([20, 10, 20])
    }
  },

  handleAxleReply(data) {
    // Special handling for Axle AI replies - find the new comment and highlight it
    setTimeout(() => {
      const axleComment = document.querySelector(`#comment-${data.axle_comment_id}`)
      
      if (axleComment) {
        // Add special Axle AI animation - golden glow
        axleComment.style.backgroundColor = 'rgba(251, 191, 36, 0.15)' // golden background
        axleComment.style.borderLeft = '3px solid #f59e0b' // golden border
        axleComment.style.transition = 'all 0.5s ease-out'
        
        // Add subtle pulsing effect
        axleComment.style.animation = 'pulse 2s ease-in-out'
        
        setTimeout(() => {
          axleComment.style.backgroundColor = 'rgba(251, 191, 36, 0.05)'
          axleComment.style.borderLeft = '2px solid #f59e0b'
          setTimeout(() => {
            axleComment.style.animation = ''
          }, 2000)
        }, 500)
        
        console.log('🤖✨ Highlighted Axle AI reply with golden glow')
      }
    }, 100)
    
    // Special notification for Axle replies
    this.showToast('🤖 Axle SI has replied', 'success')
    
    // Enhanced haptic feedback for AI replies
    if ('vibrate' in navigator) {
      navigator.vibrate([50, 30, 50, 30, 100])
    }
  },

  handleCommentVoteUpdate(data) {
    // Skip if this is the user who voted (they handle it locally)
    if (window.currentUserId && data.voter_id === window.currentUserId) {
      return
    }
    
    // Update the comment vote count for other users
    const voteElement = document.querySelector(`#comment-vote-${data.comment_id}`)
    if (voteElement) {
      const countElement = voteElement.querySelector('.vote-count, [data-voting-target="count"]')
      if (countElement) {
        const oldCount = parseInt(countElement.textContent) || 0
        countElement.textContent = data.new_score
        
        // Add a subtle animation for vote changes
        countElement.classList.add('animate-pulse')
        setTimeout(() => {
          countElement.classList.remove('animate-pulse')
        }, 600)
        
        // Update count color based on score
        countElement.classList.remove('text-red-500', 'text-gray-400', 'text-gray-500')
        if (data.new_score > 0) {
          countElement.classList.add('text-red-500')
        } else {
          countElement.classList.add(data.new_score < 0 ? 'text-gray-400' : 'text-gray-500')
        }
        
        console.log(`💖 Updated comment ${data.comment_id} vote count: ${oldCount} → ${data.new_score}`)
      }
    }
  }
  });
}
